<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { navMenuItems, brandName } from '../../config/nav.js';
import logoImg from '../../../../assets/images/logo.png';

const isMenuOpen = ref(false);
const isScrolled = ref(false);
const activeNavId = ref(null);

// 从path中提取sectionId
const getSectionIdFromPath = (path) => {
  const hashMatch = path.match(/#(\d+)/);
  return hashMatch ? hashMatch[1] : null;
};

// 更新激活状态
const updateActiveNav = () => {
  const hash = window.location.hash;
  if (hash) {
    activeNavId.value = hash.replace('#', '');
  } else {
    // 如果没有hash，检查是否是主页
    const homeItem = navMenuItems.find((item) => item.path === '/');
    if (homeItem) {
      activeNavId.value = null;
    }
  }
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  isMenuOpen.value = false;
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    window.scrollTo({
      top: element.offsetTop,
      behavior: 'smooth',
    });
  }
};

const handleNavClick = (path) => {
  // 解析路径中的 hash
  const sectionId = getSectionIdFromPath(path);
  if (sectionId) {
    // 更新激活状态
    activeNavId.value = sectionId;
    // 更新 URL hash
    window.location.hash = `#${sectionId}`;
    closeMenu();
    // 使用 setTimeout 确保菜单关闭动画完成后再滚动
    setTimeout(() => {
      scrollToSection(sectionId);
    }, 300);
  } else if (path === '/') {
    // 主页
    activeNavId.value = null;
    window.location.hash = '';
    closeMenu();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    closeMenu();
  }
};

// 检查导航项是否激活
const isActive = (path) => {
  const sectionId = getSectionIdFromPath(path);
  if (sectionId) {
    return activeNavId.value === sectionId;
  } else if (path === '/') {
    return activeNavId.value === null;
  }
  return false;
};

const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  isScrolled.value = scrollTop > 50; // 滚动超过50px时显示白色背景

  // 根据滚动位置更新激活状态
  const scrollPosition = scrollTop + 100; // 100px偏移量，提前激活

  // 检查各个section的位置
  const sections = ['1', '2', '3', '4', '5'];
  let currentSection = null;

  for (let i = sections.length - 1; i >= 0; i--) {
    const sectionId = sections[i];
    const element = document.getElementById(sectionId);
    if (element && element.offsetTop <= scrollPosition) {
      currentSection = sectionId;
      break;
    }
  }

  // 如果找到了section，更新激活状态（但不改变hash，避免影响浏览器历史）
  if (currentSection && activeNavId.value !== currentSection) {
    activeNavId.value = currentSection;
  } else if (!currentSection && activeNavId.value !== null) {
    // 如果滚动到顶部，清除激活状态
    if (scrollTop < 100) {
      activeNavId.value = null;
    }
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);

  // 页面加载时检查 URL 中的 hash，如果有则滚动到对应位置
  const handleHashScroll = () => {
    updateActiveNav();
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      nextTick(() => {
        setTimeout(() => {
          scrollToSection(sectionId);
        }, 100);
      });
    }
  };

  // 初始化激活状态
  updateActiveNav();
  handleHashScroll();

  // 监听 hash 变化（浏览器前进后退）
  window.addEventListener('hashchange', handleHashScroll);

  // 保存清理函数
  window._hashChangeHandler = handleHashScroll;
  window._scrollHandler = handleScroll;
});

onUnmounted(() => {
  if (window._scrollHandler) {
    window.removeEventListener('scroll', window._scrollHandler);
    delete window._scrollHandler;
  }
  if (window._hashChangeHandler) {
    window.removeEventListener('hashchange', window._hashChangeHandler);
    delete window._hashChangeHandler;
  }
});
</script>

<template>
  <nav class="mobile-nav">
    <div class="nav-header">
      <button
        :class="['hamburger-btn', { 'hamburger-btn-scrolled': isScrolled }]"
        @click="toggleMenu"
      >
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
      <div class="nav-center">
        <img
          :src="logoImg"
          alt="logo"
          class="logo"
        />
      </div>
    </div>

    <!-- 展开的菜单 -->
    <transition name="slide-down">
      <div
        v-if="isMenuOpen"
        class="menu-overlay"
        @click="closeMenu"
      >
        <div
          class="menu-content"
          @click.stop
        >
          <button
            class="close-btn"
            @click="closeMenu"
          >
            ×
          </button>
          <div class="menu-list">
            <a
              v-for="item in navMenuItems"
              :key="item.path"
              href="#"
              :class="['menu-item', { 'menu-item-active': isActive(item.path) }]"
              @click.prevent="handleNavClick(item.path)"
            >
              {{ item.label }}
            </a>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<style scoped lang="scss">
.mobile-nav {
  position: relative;
  z-index: 1000;
}

.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.16rem 0.4rem;
  background: #ffffff;
  border-bottom: 0.013333rem solid rgba(0, 0, 0, 0.05);
}

.hamburger-btn {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 0.6rem;
  height: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;
  transition: all 0.3s ease;
  position: relative;
}

.hamburger-btn-scrolled {
  position: fixed;
  top: 0.3rem;
  left: 0.4rem;
  width: 0.8rem;
  height: 0.8rem;
  background: #ffffff;
  border-radius: 50%;
  padding: 0.15rem;
  box-shadow: 0 0.026667rem 0.106667rem rgba(0, 0, 0, 0.1);
  z-index: 1002;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;

  .hamburger-line {
    width: 70%;
  }
}

.hamburger-line {
  width: 100%;
  height: 0.05rem;
  background: #000000;
  border-radius: 0.026667rem;
  transition: all 0.3s ease;
}

.nav-center {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logo {
  width: 1.3rem;
  height: 1.3rem;
  object-fit: contain;
}

.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.menu-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  padding: 0.266667rem;
  box-shadow: 0 0.026667rem 0.106667rem rgba(0, 0, 0, 0.1);
}

.close-btn {
  position: absolute;
  top: 0.213333rem;
  right: 0.213333rem;
  width: 0.426667rem;
  height: 0.426667rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  font-size: 0.373333rem;
  color: #666666;
  cursor: pointer;
  line-height: 1;
  padding: 0;

  &:hover {
    color: #000000;
  }
}

.menu-list {
  margin-top: 0.533333rem;
}

.menu-item {
  display: block;
  padding: 0.213333rem 0;
  font-size: 0.213333rem;
  color: #333333;
  text-decoration: none;
  border-bottom: 0.013333rem solid #e5e5e5;
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: #d4af37;
  }

  &.menu-item-active {
    color: #000000;
    background-color: #e5ad3e;
    padding-left: 0.266667rem;
    padding-right: 0.266667rem;
    margin: 0 -0.266667rem;
    border-radius: 0.053333rem;
  }
}

// 动画效果
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.slide-down-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
